import { BackendHttpService } from "../../../../../services/backendHttpService";
import { certificateDictionaryService } from "../../../../../services/dictionaries";
import { Certification } from "../models/certification.model";

export class PersonCertificationService extends BackendHttpService {
  constructor() {
    super("certification");
  }

  async getDataByPersonID(personId: number): Promise<Certification[]> {
    return this.getCertificationListByPersonId(personId);
  }

  async addData(data: Certification): Promise<Certification> {
    return this.addCertificationItem(data);
  }

  async updateData(data: Certification): Promise<Certification> {
    return this.updateCertificationItem(data);
  }

  async deleteData(data: Certification): Promise<void> {
    return this.deleteCertificationItem(data);
  }

  // Dictionary method - delegated to dictionary service
  async getCertificationDictionary(): Promise<string[]> {
    try {
      const certificates =
        await certificateDictionaryService.getCertificatesDictionary();
      return certificates.map((cert) => cert.title);
    } catch (error) {
      console.error("Error getting certification dictionary:", error);
      throw error;
    }
  }

  async getCertificationListByPersonId(personId: number): Promise<Certification[]> {
    try {
      // API returns objects with 'title' but our model expects 'name'
      interface ApiCertification {
        id: number;
        title: string;
        date: string;
        isStarred: boolean;
        expirationDate: string | null;
        personId: number;
      }

      const certifications = await this.get<ApiCertification[]>(
        `/byPersonId/${personId}`
      );
      return certifications.map(
        (cert) =>
        ({
          id: cert.id,
          name: cert.title, // Map title to name
          date: new Date(cert.date),
          isStarred: cert.isStarred,
          expirationDate: cert.expirationDate
            ? new Date(cert.expirationDate)
            : null,
          userId: cert.personId,
        } as Certification)
      );
    } catch (error) {
      console.error(
        `Error getting certification list for person ${personId}:`,
        error
      );
      throw error;
    }
  }

  async addCertificationItem(
    certification: Certification
  ): Promise<Certification> {
    try {
      // Map our model field 'name' to API expected field 'title'
      const apiCertification = {
        ...certification,
        title: certification.name,
        expirationDate: certification.expirationDate || null,
      };
      delete (apiCertification as any).name;

      const result = await this.post<typeof apiCertification, any>(
        apiCertification
      );

      // Convert back to our model format
      return {
        ...result,
        name: result.title,
        date: new Date(result.date),
        expirationDate: result.expirationDate
          ? new Date(result.expirationDate)
          : null,
      } as Certification;
    } catch (error) {
      console.error("Error adding certification item:", error);
      throw error;
    }
  }

  async updateCertificationItem(
    certification: Certification
  ): Promise<Certification> {
    try {
      // Map our model field 'name' to API expected field 'title'
      const apiCertification = {
        ...certification,
        title: certification.name,
        expirationDate: certification.expirationDate || null,
      };
      delete (apiCertification as any).name;

      const result = await this.put<typeof apiCertification, any>(
        apiCertification,
        `/${certification.id.toString()}`
      );

      // Convert back to our model format
      return {
        ...result,
        name: result.title,
        date: new Date(result.date),
        expirationDate: result.expirationDate
          ? new Date(result.expirationDate)
          : null,
      } as Certification;
    } catch (error) {
      console.error("Error updating certification item:", error);
      throw error;
    }
  }

  async deleteCertificationItem(certification: Certification): Promise<void> {
    try {
      await this.delete(`/${certification.id.toString()}`);
    } catch (error) {
      console.error("Error deleting certification item:", error);
      throw error;
    }
  }
}
